import React, { useState, useMemo } from 'react';
import Button from '@mui/material/Button';
import { Table, TableHead, TableBody, TableRow, TableCell, Paper, Typography, MenuItem, Select, Box, Modal, TextField } from '@mui/material';
import useCoreApi from '../Utils/useCoreApi';

// Helper for grouping (outside component)
function groupBySubjectYearTopic(results) {
  const groups = {};
  results.forEach(r => {
    const subj = r.lesson?.subject?.name || 'Unknown Subject';
    const year = r.lesson?.year?.year || 'Unknown Year';
    const topic = r.lesson?.topic?.name || 'Unknown Topic';
    if (!groups[subj]) groups[subj] = {};
    if (!groups[subj][year]) groups[subj][year] = {};
    if (!groups[subj][year][topic]) groups[subj][year][topic] = [];
    groups[subj][year][topic].push(r);
  });
  return groups;
}

export default function QuizResultsTable({ results }) {
  const safeResults = results || [];
  const grouped = useMemo(() => groupBySubjectYearTopic(safeResults), [safeResults]);
  // --- Selection State ---
  const subjectOptions = Object.keys(grouped || {});
  const [selectedSubject, setSelectedSubject] = useState(subjectOptions[0] || "");
  
  const yearOptions = selectedSubject ? Object.keys(grouped[selectedSubject] || {}) : [];
  const [selectedYear, setSelectedYear] = useState(yearOptions[0] || "");
  
  const topicOptions = (selectedSubject && selectedYear)
    ? Object.keys(grouped[selectedSubject]?.[selectedYear] || {})
    : [];
  const [selectedTopic, setSelectedTopic] = useState(topicOptions[0] || "");
  

  React.useEffect(() => {
    if (!subjectOptions.includes(selectedSubject)) setSelectedSubject(subjectOptions[0] || "");
  }, [subjectOptions]);
  React.useEffect(() => {
    if (!yearOptions.includes(selectedYear)) setSelectedYear(yearOptions[0] || "");
  }, [yearOptions]);
  React.useEffect(() => {
    if (!topicOptions.includes(selectedTopic)) setSelectedTopic(topicOptions[0] || "");
  }, [topicOptions]);

  const tableResults =
    grouped[selectedSubject]?.[selectedYear]?.[selectedTopic] || [];

  // --- Modal State ---
  const [openReportModal, setOpenReportModal] = useState(false);
  const [reportTarget, setReportTarget] = useState(null);
  const [reportText, setReportText] = useState('');
  const [suggestedAnswer, setSuggestedAnswer] = useState('');
  const [reportSuccess, setReportSuccess] = useState(false);
  const coreApi = useCoreApi();

  // Modal open/close handlers
  function handleOpenReportModal(row) {
    setReportTarget(row);
    setReportText('');
    setSuggestedAnswer('');
    setReportSuccess(false);
    setOpenReportModal(true);
  }
  function handleCloseReportModal() {
    setOpenReportModal(false);
  }

    // Submit report to backend
    async function handleSubmitReport() {
      if (!reportTarget?.question_id) return;
      try {
        const access_token = localStorage.getItem('accessToken');
        const reported_by = localStorage.getItem('user_id'); // <-- Get the user id
        await coreApi.post('/quiz/report_issue', {
          question_id: reportTarget.question_id,
          reported_issue_text: reportText,
          suggested_answer: suggestedAnswer || undefined,
          reported_by: reported_by ? Number(reported_by) : undefined, // Ensure it's a number if set
        });
        setReportSuccess(true);
        setTimeout(() => {
          setOpenReportModal(false);
        }, 1200);
      } catch (err) {
        let msg = 'Error submitting report. Please try again.';
        if (err?.response?.data?.error) {
          msg = err.response.data.error;
        }
        alert(msg);
      }
    }
    
  

  // Modal styling (or use your theme if you like)
  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    bgcolor: 'background.paper',
    p: 4,
    minWidth: 350,
    maxWidth: 480,
    borderRadius: 2,
    boxShadow: 24,
  };

  return (
    <Paper style={{ margin: '2rem auto', maxWidth: 1200, padding: '1rem' }}>
      <h2>Quiz Results</h2>
      <Box display="flex" gap={2} mb={3}>
        <Select
          value={selectedSubject || ""}
          onChange={e => setSelectedSubject(e.target.value)}
          displayEmpty
          style={{ minWidth: 180 }}
        >
          {subjectOptions.map(subject => (
            <MenuItem key={subject} value={subject}>{subject}</MenuItem>
          ))}
        </Select>
        <Select
          value={selectedYear || ""}
          onChange={e => setSelectedYear(e.target.value)}
          displayEmpty
          style={{ minWidth: 120 }}
          disabled={!selectedSubject}
        >
          {yearOptions.map(year => (
            <MenuItem key={year} value={year}>Year {year}</MenuItem>
          ))}
        </Select>
        <Select
          value={selectedTopic || ""}
          onChange={e => setSelectedTopic(e.target.value)}
          displayEmpty
          style={{ minWidth: 220 }}
          disabled={!selectedYear}
        >
          {topicOptions.map(topic => (
            <MenuItem key={topic} value={topic}>{topic}</MenuItem>
          ))}
        </Select>
      </Box>

      {tableResults.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No quiz results for this selection.
        </Typography>
      ) : (
        <>
          <Typography variant="subtitle1" gutterBottom>
            <b>Subject:</b> {selectedSubject} &nbsp;
            <b>Year:</b> {selectedYear} &nbsp;
            <b>Topic:</b> {selectedTopic}
          </Typography>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Lesson</TableCell>
                <TableCell>Question</TableCell>
                <TableCell>Correct Answer</TableCell>
                <TableCell>Your Answer</TableCell>
                <TableCell>Correct?</TableCell>
                <TableCell>Skipped?</TableCell>
                <TableCell>Attempts</TableCell>
                <TableCell>Answered At</TableCell>
                <TableCell>Report Issue</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tableResults.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>{r.lesson?.title || '-'}</TableCell>
                  <TableCell>{r.question?.question_text || '-'}</TableCell>
                  <TableCell>{r.question?.answer || '-'}</TableCell>
                  <TableCell>
                    {r.selected_answer === null
                      ? <span style={{ color: '#aaa' }}>No answer</span>
                      : r.selected_answer}
                  </TableCell>
                  <TableCell>
                    {r.is_correct ? <span style={{ color: 'green' }}>Yes</span> : <span style={{ color: 'red' }}>No</span>}
                  </TableCell>
                  <TableCell>
                    {r.skipped ? "Yes" : "No"}
                  </TableCell>
                  <TableCell>{r.attempt}</TableCell>
                  <TableCell>{new Date(r.answered_at).toLocaleString()}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      onClick={() => handleOpenReportModal(r)}
                      disabled={!r.question_id}
                    >
                      Report
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}

      {/* Report Modal */}
      <Modal open={openReportModal} onClose={handleCloseReportModal}>
        <Box sx={modalStyle}>
          {!reportSuccess ? (
            <>
              <Typography variant="h6" mb={2}>Report an Issue</Typography>
              <Typography variant="body2" color="textSecondary" mb={1}>
                <b>Question:</b> {reportTarget?.question?.question_text}
              </Typography>
              <TextField
                label="Describe the issue"
                variant="outlined"
                fullWidth
                multiline
                rows={3}
                value={reportText}
                onChange={e => setReportText(e.target.value)}
                sx={{ my: 1 }}
              />
              <TextField
                label="Suggested Correct Answer (optional)"
                variant="outlined"
                fullWidth
                value={suggestedAnswer}
                onChange={e => setSuggestedAnswer(e.target.value)}
                sx={{ my: 1 }}
              />
              <Box mt={2} display="flex" justifyContent="flex-end" gap={1}>
                <Button onClick={handleCloseReportModal}>Cancel</Button>
                <Button
                  variant="contained"
                  color="warning"
                  onClick={handleSubmitReport}
                  disabled={!reportText.trim()}
                >
                  Submit Report
                </Button>
              </Box>
            </>
          ) : (
            <Typography variant="h6" color="success.main" align="center">
              Thank you! Your report has been submitted.
            </Typography>
          )}
        </Box>
      </Modal>
    </Paper>
  );
}
